import React, { forwardRef, useRef, useState, useEffect, useImperativeHandle } from 'react';

export interface TkiQrcodeProps {
  size?: number;
  unit?: 'upx' | 'px';
  show?: boolean;
  val?: string;
  background?: string;
  foreground?: string;
  icon?: string;
  iconSize?: number;
  lv?: number;
  onval?: boolean;
  loadMake?: boolean;
  onResult?: (imgBase64: string) => void;
}

export interface TkiQrcodeRef {
  _makeCode: (v?: string) => void;
  _clearCode: () => void;
  _saveCode: () => void;
}

const upx2px = (val: number) => {
  const width = document.documentElement.clientWidth || 375;
  return (val / 750) * width;
};

const TkiQrcode = forwardRef<TkiQrcodeRef, TkiQrcodeProps>((props, ref) => {
  const {
    size = 200,
    unit = 'upx',
    show = true,
    val = '',
    icon = '',
    iconSize = 40,
    onval = false,
    loadMake = false,
    onResult,
  } = props;

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [imgUrl, setImgUrl] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  const realSize = unit === 'upx' ? upx2px(size) : size;

  const generateQr = async (text: string) => {
    if (!text) {
      alert('二维码内容不能为空');
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) return;

    const QRCodeLib = await import('qrcode');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = realSize;
    canvas.height = realSize;

    await QRCodeLib.default.toCanvas(canvas, text, {
      width: realSize,
      margin: 2,
      color: {
        dark: '#000000', // 方块黑
        light: '#ffffff', // 背景白
      },
      errorCorrectionLevel: 'H',
    });

    if (icon) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = icon;
      img.onload = () => {
        const ix = realSize / 2 - iconSize / 2;
        const iy = realSize / 2 - iconSize / 2;
        ctx.drawImage(img, ix, iy, iconSize, iconSize);
        const base64 = canvas.toDataURL('image/png');
        setImgUrl(base64);
        onResult?.(base64);
      };
    } else {
      const base64 = canvas.toDataURL('image/png');
      setImgUrl(base64);
      onResult?.(base64);
    }
  };

  const _makeCode = (v?: string) => {
    generateQr(v || val || '');
  };

  const _clearCode = () => {
    setImgUrl('');
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx?.clearRect(0, 0, canvas.width, canvas.height);
    }
  };

  const _saveCode = () => {
    if (!imgUrl) return;
    const a = document.createElement('a');
    a.href = imgUrl;
    a.download = `qrcode_${Date.now()}.png`;
    a.click();
  };

  useImperativeHandle(ref, () => ({
    _makeCode,
    _clearCode,
    _saveCode,
  }));

  useEffect(() => {
    if (loadMake && val) {
      generateQr(val);
    }
  }, []); //eslint-disable-line

  useEffect(() => {
    if (onval && val) {
      generateQr(val);
    }
  }, [val, onval]); //eslint-disable-line

  return (
    <div style={{ position: 'relative' }}>
      <canvas ref={canvasRef} style={{ position: 'fixed', top: -9999, left: -9999, zIndex: -9999 }} />

      {show && imgUrl && (
        <img
          src={imgUrl}
          style={{ width: realSize, height: realSize }}
          onClick={() => setShowPreview(true)}
          alt="qrcode"
        />
      )}

      {showPreview && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
          onClick={() => setShowPreview(false)}
        >
          <img src={imgUrl} style={{ width: '300px', height: '300px', background: '#fff' }} alt="preview" />
        </div>
      )}
    </div>
  );
});

export default TkiQrcode;
